import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Palette,
  Image,
  Type,
  Shield,
  Fingerprint,
  FileText,
  CheckCircle2,
  Building2,
  User,
  DollarSign,
  CreditCard,
  Briefcase,
  MousePointer2
} from "lucide-react";
import { Button } from "../components/ui/button";

const BRANDS = [
  { name: "Your Brand", primary: "#6366f1", bg: "#0f0f2e", surface: "#1a1a40", accent: "#a5b4fc", logo: "YB" },
  { name: "Coral Bank", primary: "#E85D75", bg: "#1a0f14", surface: "#2a1520", accent: "#FF9EB5", logo: "CB" },
  { name: "Island Credit", primary: "#10B981", bg: "#0a1a14", surface: "#112920", accent: "#6EE7B7", logo: "IC" },
];

const STEPS = [
  {
    id: 1,
    title: "Welcome & Disclaimer",
    desc: "Branded welcome screen with your logo, colors, and compliance disclaimers.",
    icon: Building2,
    annotations: [
      { label: "Your logo here", x: "12%", y: "12%", icon: Image },
      { label: "Brand colors", x: "70%", y: "8%", icon: Palette },
    ],
    content: (brand) => (
      <div className="p-6 md:p-8 text-center">
        <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center text-xl font-black text-white" style={{ background: brand.primary }}>
          {brand.logo}
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Welcome to {brand.name}</h3>
        <p className="text-sm text-slate-400 mb-4">Pre-qualify for a loan in minutes. Your information is secure and encrypted.</p>
        <div className="rounded-lg p-3 mb-4 border" style={{ background: `${brand.primary}10`, borderColor: `${brand.primary}30` }}>
          <p className="text-xs" style={{ color: brand.primary }}>{brand.name} is a licensed financial service provider. This is a pre-qualification estimate only.</p>
        </div>
        <div className="w-full h-11 rounded-xl flex items-center justify-center text-sm font-semibold text-white" style={{ background: brand.primary }}>
          I Understand, Continue
        </div>
      </div>
    ),
  },
  {
    id: 2,
    title: "Employment & Loan Type",
    desc: "Applicant selects employment type and loan product. Fully configurable product list.",
    icon: Briefcase,
    annotations: [
      { label: "Custom loan products", x: "65%", y: "55%", icon: Type },
    ],
    content: (brand) => (
      <div className="p-6 md:p-8">
        <p className="text-xs mb-1" style={{ color: brand.primary }}>Step 1 of 5</p>
        <h3 className="text-lg font-bold text-white mb-4">What type of employment?</h3>
        <div className="grid grid-cols-3 gap-2 mb-6">
          {["Government", "Private", "Self-Employed"].map((t, i) => (
            <div key={t} className={`p-3 rounded-xl text-center text-xs font-medium border cursor-pointer transition-all ${i === 0 ? "text-white" : "text-slate-500 border-white/10"}`}
              style={i === 0 ? { background: `${brand.primary}20`, borderColor: `${brand.primary}50`, color: brand.primary } : {}}>
              {t}
            </div>
          ))}
        </div>
        <h3 className="text-lg font-bold text-white mb-3">What are you applying for?</h3>
        <div className="grid grid-cols-2 gap-2">
          {[
            { label: "Personal Loan", icon: DollarSign },
            { label: "Mortgage", icon: Building2 },
            { label: "Auto Loan", icon: CreditCard },
            { label: "Business Loan", icon: Briefcase },
          ].map((p, i) => (
            <div key={p.label} className={`flex items-center gap-2 p-3 rounded-xl border text-xs font-medium transition-all ${i === 0 ? "text-white" : "text-slate-500 border-white/10"}`}
              style={i === 0 ? { background: `${brand.primary}15`, borderColor: `${brand.primary}40`, color: brand.accent } : {}}>
              <p.icon className="w-4 h-4" />
              {p.label}
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: 3,
    title: "Financial Details",
    desc: "Income, debts, and desired loan amount — auto-calculates TDSR and eligibility in real time.",
    icon: DollarSign,
    annotations: [
      { label: "Real-time TDSR", x: "68%", y: "72%", icon: Type },
    ],
    content: (brand) => (
      <div className="p-6 md:p-8">
        <p className="text-xs mb-1" style={{ color: brand.primary }}>Step 2 of 5</p>
        <h3 className="text-lg font-bold text-white mb-4">Financial Information</h3>
        <div className="space-y-3">
          {[
            { label: "Monthly Salary", value: "$4,500" },
            { label: "Other Income", value: "$0" },
            { label: "Credit Card Payments", value: "$200" },
            { label: "Existing Loan Payments", value: "$350" },
            { label: "Desired Loan Amount", value: "$35,000" },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-center justify-between p-3 rounded-lg border border-white/[0.06]" style={{ background: `${brand.bg}` }}>
              <span className="text-xs text-slate-400">{label}</span>
              <span className="text-sm font-semibold text-white">{value}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 p-3 rounded-xl border" style={{ background: `${brand.primary}10`, borderColor: `${brand.primary}30` }}>
          <div className="flex justify-between items-center">
            <span className="text-xs text-slate-400">Estimated TDSR</span>
            <span className="text-sm font-bold" style={{ color: brand.primary }}>38.2%</span>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 4,
    title: "KYC Verification",
    desc: "Embedded identity verification — document upload, liveness check, sanctions & PEP screening.",
    icon: Fingerprint,
    annotations: [
      { label: "Sumsub embedded", x: "10%", y: "75%", icon: Shield },
      { label: "Your branding", x: "60%", y: "10%", icon: Palette },
    ],
    content: (brand) => (
      <div className="p-6 md:p-8 text-center">
        <p className="text-xs mb-1" style={{ color: brand.primary }}>Step 3 of 5</p>
        <h3 className="text-lg font-bold text-white mb-2">Identity Verification</h3>
        <p className="text-xs text-slate-400 mb-5">This secure step usually takes 2–3 minutes.</p>
        <div className="rounded-xl border border-white/[0.08] p-6" style={{ background: brand.bg }}>
          <div className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center" style={{ background: `${brand.primary}20` }}>
            <Fingerprint className="w-6 h-6" style={{ color: brand.primary }} />
          </div>
          <div className="space-y-2">
            {["Upload Government ID", "Liveness Check", "Sanctions Screening", "PEP Database Check"].map((s, i) => (
              <div key={s} className="flex items-center gap-2 justify-center">
                <CheckCircle2 className="w-3.5 h-3.5" style={{ color: i < 2 ? brand.primary : "rgb(100,116,139)" }} />
                <span className={`text-xs ${i < 2 ? "text-white" : "text-slate-500"}`}>{s}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-center gap-2 mt-4 text-[10px] text-slate-600">
          <Shield className="w-3 h-3" /> Powered by Sumsub · Encrypted
        </div>
      </div>
    ),
  },
  {
    id: 5,
    title: "Approved Products",
    desc: "Show the applicant which of your institution's products they qualify for — amounts, rates, and terms.",
    icon: CheckCircle2,
    annotations: [
      { label: "Your products", x: "65%", y: "12%", icon: Type },
      { label: "Custom styling", x: "10%", y: "8%", icon: Palette },
    ],
    content: (brand) => (
      <div className="p-6 md:p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-black text-white" style={{ background: brand.primary }}>{brand.logo}</div>
          <div>
            <p className="text-xs text-slate-500">Results from</p>
            <p className="text-sm font-bold text-white">{brand.name}</p>
          </div>
        </div>
        <div className="rounded-xl p-3 mb-4 border" style={{ background: `${brand.primary}10`, borderColor: `${brand.primary}30` }}>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" style={{ color: brand.primary }} />
            <span className="text-sm font-semibold text-white">Congratulations — you qualify for the following:</span>
          </div>
        </div>
        <div className="space-y-2">
          {[
            { product: "Home Mortgage", amount: "$150,000", rate: "4.0%", term: "30 yrs", icon: "🏠" },
            { product: "Auto Loan", amount: "$25,000", rate: "5.5%", term: "7 yrs", icon: "🚗" },
            { product: "Visa Credit Card", amount: "$10,000 limit", rate: "18.9% APR", term: "Revolving", icon: "💳" },
            { product: "Personal Loan", amount: "$15,000", rate: "12.0%", term: "5 yrs", icon: "💰" },
          ].map((r) => (
            <div key={r.product} className="flex items-center justify-between p-3 rounded-xl border border-white/[0.06]" style={{ background: brand.bg }}>
              <div className="flex items-center gap-3">
                <span className="text-lg">{r.icon}</span>
                <div>
                  <p className="text-sm font-semibold text-white">{r.product}</p>
                  <p className="text-[10px] text-slate-500">{r.rate} · {r.term}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold" style={{ color: brand.primary }}>{r.amount}</p>
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">
                  Approved
                </span>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-slate-600 text-center mt-4">Products and amounts are determined by {brand.name}'s lending criteria.</p>
      </div>
    ),
  },
];

export default function LendDemoPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [brandIndex, setBrandIndex] = useState(0);

  const brand = BRANDS[brandIndex];
  const step = STEPS[currentStep];

  const nextStep = () => currentStep < STEPS.length - 1 && setCurrentStep(s => s + 1);
  const prevStep = () => currentStep > 0 && setCurrentStep(s => s - 1);
  const nextBrand = () => setBrandIndex(i => (i + 1) % BRANDS.length);

  return (
    <div className="bg-[#0a1628] min-h-screen" style={{ fontFamily: "Inter, sans-serif" }}>
      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate("/partners/verify")} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#52BFB8] transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> Partners
          </button>
          <span className="text-[10px] text-slate-600 tracking-wider uppercase">Interactive Walkthrough</span>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight mb-2" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
            Financify Lend<span className="text-[#52BFB8]">.</span> Walkthrough
          </h1>
          <p className="text-sm text-slate-400">See how the white-label loan application flow works — slide through each step.</p>
        </div>

        {/* Brand switcher */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <span className="text-xs text-slate-500">Brand:</span>
          <button onClick={nextBrand} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all" style={{ background: `${brand.primary}15`, borderColor: `${brand.primary}30`, color: brand.primary }} data-testid="brand-switcher">
            <div className="w-4 h-4 rounded flex items-center justify-center text-[8px] font-black text-white" style={{ background: brand.primary }}>{brand.logo}</div>
            <span className="text-xs font-semibold">{brand.name}</span>
            <Palette className="w-3 h-3" />
          </button>
          <span className="text-[10px] text-slate-600">Click to change</span>
        </div>

        {/* Main walkthrough card */}
        <div className="grid lg:grid-cols-[1fr,320px] gap-6">

          {/* Preview */}
          <div className="relative">
            <div className="rounded-2xl overflow-hidden border shadow-2xl" style={{ background: brand.surface, borderColor: `${brand.primary}20` }}>
              {/* Window chrome */}
              <div className="flex items-center justify-between px-4 py-2 border-b" style={{ background: brand.bg, borderColor: "rgba(255,255,255,0.06)" }}>
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                  <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                  <span className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                </div>
                <span className="text-[10px] text-slate-600">{brand.name.toLowerCase().replace(/\s/g, "")}.financify.io</span>
                <div className="w-12" />
              </div>

              {/* Step content */}
              <div className="min-h-[380px]">
                {step.content(brand)}
              </div>
            </div>

            {/* Annotations */}
            {step.annotations.map((a, i) => (
              <div key={i} className="absolute z-20 animate-pulse" style={{ left: a.x, top: a.y }}>
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#FDD552] text-[#0a1628] shadow-lg">
                  <MousePointer2 className="w-3 h-3" />
                  <span className="text-[10px] font-bold whitespace-nowrap">{a.label}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Step info sidebar */}
          <div className="space-y-4">
            {/* Step list */}
            <div className="rounded-2xl bg-[#111d32]/50 border border-white/[0.06] p-5">
              <p className="text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-500 mb-3">Application Flow</p>
              <div className="space-y-1">
                {STEPS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentStep(i)}
                    data-testid={`step-nav-${i}`}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-all ${
                      i === currentStep ? "bg-[#52BFB8]/10 border border-[#52BFB8]/30" : "hover:bg-white/[0.03] border border-transparent"
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      i < currentStep ? "bg-[#52BFB8] text-[#0a1628]" :
                      i === currentStep ? "bg-[#52BFB8]/20 text-[#52BFB8]" :
                      "bg-white/5 text-slate-600"
                    }`}>
                      {i < currentStep ? <CheckCircle2 className="w-3.5 h-3.5" /> : <s.icon className="w-3.5 h-3.5" />}
                    </div>
                    <span className={`text-xs font-medium ${i === currentStep ? "text-white" : "text-slate-500"}`}>{s.title}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Current step description */}
            <div className="rounded-2xl bg-[#111d32]/50 border border-white/[0.06] p-5">
              <h3 className="text-sm font-semibold text-white mb-2">{step.title}</h3>
              <p className="text-xs text-slate-400 leading-relaxed">{step.desc}</p>
            </div>

            {/* CTA */}
            <Button
              onClick={() => navigate("/book-demo")}
              className="w-full bg-[#52BFB8] hover:bg-[#3ea8a1] text-[#0a1628] font-semibold py-5 rounded-xl"
              data-testid="walkthrough-cta"
            >
              Schedule a Demo
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Navigation arrows */}
        <div className="flex items-center justify-center gap-4 mt-6">
          <button onClick={prevStep} disabled={currentStep === 0} className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-white hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-testid="prev-step">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-xs text-slate-500">Step {currentStep + 1} of {STEPS.length}</span>
          <button onClick={nextStep} disabled={currentStep === STEPS.length - 1} className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-white hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-testid="next-step">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
